import _ from "lodash";
import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Popover } from "@toddle-edu/react-tiny-popover";

import {
  BentArrowTopLeftOutlined,
  SmileyHappyOutlined,
  DotsVerticalOutlined,
  AddOutlined,
  PencilLineOutlined,
  DeleteOutlined,
  MarkAsReadOutlined,
} from "@toddle-edu/ds-icons";
import {
  Button,
  Dropdown,
  DropdownMenu,
  IconButton,
  Dialog,
  Tooltip,
} from "@toddle-edu/ds-web";
import { elevations } from "@toddle-edu/ds-theme";

import { useI18n } from "Hooks";
import {
  getParentContainer,
  scrollOnlyIfNearBottom,
  MESSAGE_STATUS,
} from "Utils/MessageHelpers";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { useMessageOptions } from "UIComponents/Message/providers/MessageProvider";
import { formatDate } from "Utils";

import ReadReceiptModal from "AppComponents/ReadReceiptModal";
import ReadReceipt from "../ReadReceipt";
import classes from "./MessageOptions.scss";

const MESSAGE_ACTIONS = {
  REPLY: "REPLY",
  EDIT: "EDIT",
  DELETE: "DELETE",
};

export const DROPDOWN_TYPES = {
  MORE_OPTIONS: "MORE_OPTIONS",
  EMOJI_PANEL: "EMOJI_PANEL",
};

const MORE_OPTIONS_DROPDOWN_KEYS = {
  EDIT: "edit",
  DELETE: "delete",
  READ_RECEIPT: "read-receipt",
};

const PANEL_EMOJIS = [
  {
    id: "+1",
    colons: ":+1:",
    unified: "1f44d",
    native: "👍",
  },
  {
    id: "innocent",
    colons: ":innocent:",
    unified: "1f607",
    native: "😇",
  },
  {
    id: "joy",
    colons: ":joy:",
    unified: "1f602",
    native: "😂",
  },
  {
    id: "-1",
    colons: ":-1:",
    unified: "1f44e",
    native: "👎",
  },
  {
    id: "clap",
    colons: ":clap:",
    unified: "1f44f",
    native: "👏",
  },
];

const DROPDOWN_OPTIONS = [
  {
    key: MORE_OPTIONS_DROPDOWN_KEYS.EDIT,
    label: "common:edit",
    isOptionHidden: ({ isMessageSent, canModifyMessage }) =>
      !isMessageSent || !canModifyMessage,
    icon: <PencilLineOutlined />,
  },
  {
    key: MORE_OPTIONS_DROPDOWN_KEYS.READ_RECEIPT,
    label: "common:read_receipts",
    isOptionHidden: ({ isMessageSent, isChannel }) =>
      !isMessageSent || !isChannel,
    icon: <MarkAsReadOutlined />, // TODO: change icon
  },
  {
    key: "Divider",
    isDivider: true,
    isOptionHidden: ({ canModifyMessage }) => !canModifyMessage,
  },
  {
    key: MORE_OPTIONS_DROPDOWN_KEYS.DELETE,
    isOptionHidden: ({ canModifyMessage }) => !canModifyMessage,
    label: "common:delete",
    isDestructive: true,
    icon: <DeleteOutlined />,
  },
];

const getDropdownOptions = ({
  t,
  isMessageSent,
  isChannel,
  canModifyMessage,
}) => {
  const options = _.reduce(
    DROPDOWN_OPTIONS,
    (acc, option) => {
      const { label, isOptionHidden, ...rest } = option;
      const isHidden = _.isFunction(isOptionHidden)
        ? isOptionHidden({ isMessageSent, isChannel, canModifyMessage })
        : false;

      if (isHidden) return acc;

      if (option.isDivider) acc.push(option);
      else acc.push({ label: t(label), ...rest });

      return acc;
    },
    []
  );

  return options;
};
const MessageOptions = props => {
  const {
    isSender,
    isChannel,
    message,
    toggleReaction,
    openedDropdown,
    setOpenedDropdown,
    onEditMessageClick,
    onReplyMessageClick,
    deleteMessage,
    setMainEmojiPickerState,
    status,
    shouldSeparateMessage = false,
    canModifyMessage = true,
    recordMessageActions,
    getPopupContainer,
    showReplyOption,
    showReactOption,
    showReadReceipt,
    allowDeleteForAllMessages = false,
  } = props;

  const isMessageSent = _.isUndefined(status);

  const { t } = useI18n(["common", "classRoom"]);
  const { chatId, conversationId, parentKey } = useMessageOptions();
  const parentId = chatId || conversationId;

  const containerRef = useRef(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isReadReceiptModalOpen, setIsReadReceiptModalOpen] = useState(false);

  const handleReplyClick = () => {
    onReplyMessageClick({ message });
    scrollOnlyIfNearBottom({ parentId });
    recordMessageActions?.({
      type: MESSAGE_ACTIONS.REPLY,
      messageId: message.id,
    });
  };

  const closeOpenedDropdown = () => setOpenedDropdown(null);

  // function to open mini emoji panel
  const toggleEmojiPanel = () => {
    setOpenedDropdown(prev =>
      prev === DROPDOWN_TYPES.EMOJI_PANEL ? null : DROPDOWN_TYPES.EMOJI_PANEL
    );
  };

  // function to open main emoji picker
  const openMainEmojiPicker = e => {
    /**
      Note: Do not remove the following line.
      It is required to prevent the the click event from bubbling up to the container,
      which would otherwise close the emoji popover. Since the button triggering
      the event would be destroyed before the event reaches the container,
      the dropdown would incorrectly interpret the click as occurring outside of it.
    */
    e.stopPropagation();

    closeOpenedDropdown();
    setMainEmojiPickerState({ message, element: containerRef.current });
  };

  const handleReadReceiptModalClose = () => {
    setIsReadReceiptModalOpen(false);
    closeOpenedDropdown();
  };

  const handleMoreOptionsVisibilityChange = visible => {
    setOpenedDropdown(visible ? DROPDOWN_TYPES.MORE_OPTIONS : null);
  };

  const closeDeleteDialog = () => {
    setIsDeleteModalOpen(false);
    closeOpenedDropdown();
  };

  const onConfirmDelete = () => {
    deleteMessage({ [parentKey]: parentId, messageId: message.id });
    closeDeleteDialog();
  };

  const onMoreOptionsClick = ({ key }) => {
    switch (key) {
      case MORE_OPTIONS_DROPDOWN_KEYS.EDIT:
        onEditMessageClick({ message });
        closeOpenedDropdown();
        recordMessageActions?.({
          type: MESSAGE_ACTIONS.EDIT,
          messageId: message.id,
        });
        break;
      case MORE_OPTIONS_DROPDOWN_KEYS.DELETE:
        setIsDeleteModalOpen(true);
        recordMessageActions?.({
          type: MESSAGE_ACTIONS.DELETE,
          messageId: message.id,
        });
        break;
      case MORE_OPTIONS_DROPDOWN_KEYS.READ_RECEIPT:
        setIsReadReceiptModalOpen(true);
        break;
      default:
        break;
    }
  };

  // handle emoji click on mini emoji panel
  const handleReactionClick = e => {
    const idx = _.get(e, "currentTarget.dataset.idx");
    const emoji = PANEL_EMOJIS[idx];

    toggleReaction({
      messageId: message.id,
      emojiObject: emoji,
      [parentKey]: parentId,
    });
    closeOpenedDropdown();
  };

  const reactionPanelContent = (
    <div
      className={classes.popoverInnerContainerV2}
      style={elevations["elevation-2B"]}
    >
      {_.map(PANEL_EMOJIS, (emoji, idx) => (
        <Button
          key={emoji.id}
          size={"small"}
          variant="plain"
          className={classes.emojiButtonV2}
          data-idx={idx}
          onClick={handleReactionClick}
        >
          <div className={"heading-6"}>{emoji.native}</div>
        </Button>
      ))}
      <IconButton
        size={"small"}
        variant="plain"
        className={classes.emojiButtonV2}
        icon={<AddOutlined size={"xxx-small"} />}
        onClick={openMainEmojiPicker}
      />
    </div>
  );

  const commonIconContainerProps = {
    size: "x-small",
    variant: "plain",
    className: classes.iconContainerV2,
  };

  const iconSize = "xxx-small";
  const dropdownOptions = getDropdownOptions({
    t,
    isMessageSent,
    isChannel,
    canModifyMessage,
  });

  const dateClass = classNames("text-body-3", classes.date);
  const dateReceiptContainerClasses = classNames({
    [classes.dateReceiptContainer]: true,
    [classes.dateReceiptContainerWithoutSeparator]: !(
      showReactOption ||
      showReplyOption ||
      (isSender && !_.isEmpty(dropdownOptions))
    ),
  });

  const showDateAndReceipt = !shouldSeparateMessage;
  const scrollContainer = getParentContainer(parentId);
  const isMoreOptionsVisible = openedDropdown === DROPDOWN_TYPES.MORE_OPTIONS;
  const isReactionsPanelOpen = openedDropdown === DROPDOWN_TYPES.EMOJI_PANEL;

  if (
    !(
      showReplyOption ||
      showReactOption ||
      showReadReceipt ||
      isSender ||
      showDateAndReceipt ||
      allowDeleteForAllMessages
    )
  ) {
    return null;
  }

  return (
    <div ref={containerRef} className={classes.containerV2}>
      {isMessageSent ? (
        <>
          {showDateAndReceipt ? (
            <div className={dateReceiptContainerClasses}>
              <div className={dateClass}>
                {formatDate({
                  date: message?.createdAt,
                  format: DATE_TIME_CONSTANTS.SENT_MESSAGE,
                })}
              </div>
              {showReadReceipt ? (
                <ReadReceipt
                  isSender={isSender}
                  isSent={isMessageSent}
                  isRead={message?.isRead}
                />
              ) : null}
            </div>
          ) : null}

          {showReplyOption ? (
            <Tooltip
              placement="top"
              tooltip={<div className="text-tooltip">{t("common:reply")}</div>}
            >
              <IconButton
                {...commonIconContainerProps}
                onClick={handleReplyClick}
                aria-label="reply message"
                icon={
                  <BentArrowTopLeftOutlined size={iconSize} variant="subtle" />
                }
              />
            </Tooltip>
          ) : null}

          {showReactOption ? (
            <Popover
              reposition
              isOpen={isReactionsPanelOpen}
              content={reactionPanelContent}
              positions={["top", "bottom"]}
              containerParent={scrollContainer}
              align={["start", "end"]}
              containerClassName={classes.popoverContainer}
              onClickOutside={closeOpenedDropdown}
            >
              <div>
                <Tooltip
                  placement="top"
                  tooltip={
                    <div className="text-tooltip">
                      {t("common:add_reaction")}
                    </div>
                  }
                >
                  <IconButton
                    {...commonIconContainerProps}
                    onClick={toggleEmojiPanel}
                    aria-label="open reactions panel"
                    icon={
                      <SmileyHappyOutlined size={iconSize} variant="subtle" />
                    }
                  />
                </Tooltip>
              </div>
            </Popover>
          ) : null}
        </>
      ) : null}

      {isSender && !_.isEmpty(dropdownOptions) ? (
        <Dropdown
          visible={isMoreOptionsVisible}
          onVisibleChange={handleMoreOptionsVisibilityChange}
          trigger={["click"]}
          {...{ align: { offset: [0, 8] } }}
          overlay={
            <DropdownMenu
              options={dropdownOptions}
              onClick={onMoreOptionsClick}
              dsVersion={"2.0"}
            />
          }
        >
          <div>
            <Tooltip
              placement="top"
              tooltip={<div className="text-tooltip">{t("common:more")}</div>}
            >
              <IconButton
                {...commonIconContainerProps}
                aria-label="more options"
                icon={<DotsVerticalOutlined size={iconSize} variant="subtle" />}
              />
            </Tooltip>
          </div>
        </Dropdown>
      ) : !isSender && allowDeleteForAllMessages ? (
        <IconButton
          {...commonIconContainerProps}
          aria-label="delete button"
          onClick={() =>
            onMoreOptionsClick({ key: MORE_OPTIONS_DROPDOWN_KEYS.DELETE })
          }
          icon={<DeleteOutlined size={iconSize} variant="critical" />}
        />
      ) : null}

      {isDeleteModalOpen ? (
        <Dialog
          isOpen
          title={t("common:delete_message")}
          message={t("classRoom:delete_message_alert")}
          variant="delete-dialog"
          onClose={closeDeleteDialog}
          onButtonOneClick={closeDeleteDialog}
          onButtonTwoClick={onConfirmDelete}
        />
      ) : null}
      {isReadReceiptModalOpen ? (
        <ReadReceiptModal
          onCloseModal={handleReadReceiptModalClose}
          message={message}
          isMessageV2={!_.isEmpty(chatId)}
        />
      ) : null}
    </div>
  );
};

MessageOptions.propTypes = {
  message: PropTypes.object.isRequired,
  isSender: PropTypes.bool.isRequired,
  isChannel: PropTypes.bool.isRequired,
  openedDropdown: PropTypes.oneOf(_.values(DROPDOWN_TYPES)),
  toggleReaction: PropTypes.func,
  setOpenedDropdown: PropTypes.func.isRequired,
  onEditMessageClick: PropTypes.func,
  onReplyMessageClick: PropTypes.func,
  deleteMessage: PropTypes.func.isRequired,
  setMainEmojiPickerState: PropTypes.func,
  status: PropTypes.oneOf(_.values(MESSAGE_STATUS)),
  recordMessageActions: PropTypes.func,
  getPopupContainer: PropTypes.func.isRequired,
  showReplyOption: PropTypes.bool.isRequired,
  showReactOption: PropTypes.bool.isRequired,
  showReadReceipt: PropTypes.bool.isRequired,
  allowDeleteForAllMessages: PropTypes.bool,
};

MessageOptions.defaultProps = {
  openedDropdown: null,
  getPopupContainer: () => document.body,
};

export default MessageOptions;
